import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, FileDown, Loader2, Store } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { PageHeader } from "@/pages/admin/AdminLayout";
import { buildWeekPdf, downloadBlob } from "@/lib/pdf";

const DOW = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const shift = (s, n) => { const d = new Date(`${s}T12:00:00`); d.setDate(d.getDate() + n); return iso(d); };
const mins = (t) => { const [h, m] = t.split(":").map(Number); return h * 60 + m; };
const dm = (s) => `${s.slice(8, 10)}/${s.slice(5, 7)}`;

// ids of employees whose times overlap in the same day
function overlapIds(data, day) {
  const slots = [];
  for (const r of data.rows) for (const e of r.cells[day] || []) for (const p of e.people) slots.push([p.id, mins(e.start), mins(e.end), e.job_id, r.shop ? "shop" : "site"]);
  for (const e of data.interventions[day] || []) for (const p of e.people) slots.push([p.id, mins(e.start), mins(e.end), e.job_id, "int"]);
  const set = new Set();
  const ok = (a, b) => [a[4], b[4]].sort().join() === "int,shop";
  slots.forEach((a, i) => slots.forEach((b, j) => { if (i < j && a[0] === b[0] && a[3] !== b[3] && !ok(a, b) && a[1] < b[2] && b[1] < a[2]) set.add(a[0]); }));
  return set;
}

const uniq = (list) => [...new Map(list.map((p) => [p.id, p])).values()];

function EditCell({ cell, staff, onClose, onSaved }) {
  const [sel, setSel] = useState([]);
  const [step, setStep] = useState("pick");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (cell) { setSel(cell.people.map((p) => p.id)); setStep("pick"); } }, [cell]);
  if (!cell) return null;
  const lead = new Set(cell.people.filter((p) => p.lead).map((p) => p.id));
  const changed = sel.length !== cell.people.length || sel.some((id) => !cell.people.some((p) => p.id === id));
  const save = async (notify) => {
    setBusy(true);
    try {
      await api.put("/admin/planner/cell", { series_id: cell.row.series_id, date: cell.day, member_ids: sel, notify });
      toast.success(notify ? "Salvato e promemoria inviato" : "Salvato");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-sm" data-testid="planner-edit-dialog">
        <DialogHeader><DialogTitle>{cell.row.name} · {DOW[cell.idx]} {dm(cell.day)}</DialogTitle></DialogHeader>
        {step === "pick" ? (
          <>
            <div className="grid max-h-80 gap-1 overflow-y-auto">
              {staff.map((s) => (
                <label key={s.id} className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${sel.includes(s.id) ? "border-[#61a229] bg-[#f2f7ec]" : ""}`}>
                  <input type="checkbox" checked={sel.includes(s.id)} disabled={lead.has(s.id)} data-testid={`planner-staff-${s.id}`}
                    onChange={() => setSel((x) => (x.includes(s.id) ? x.filter((i) => i !== s.id) : [...x, s.id]))} />
                  {s.name}
                </label>
              ))}
            </div>
            <DialogFooter><Button className="w-full rounded-full" disabled={!changed} onClick={() => setStep("ask")} data-testid="planner-save-button">Salva</Button></DialogFooter>
          </>
        ) : (
          <div className="space-y-3" data-testid="planner-notify-dialog">
            <p className="text-sm">Mandare ai nuovi assegnati il promemoria <b>«{cell.day === shift(iso(new Date()), 1) ? "Domani" : `Il ${dm(cell.day)}`} sei assegnato al {cell.row.name}»</b>?</p>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" className="rounded-full" disabled={busy} onClick={() => save(false)} data-testid="planner-notify-no">No</Button>
              <Button className="rounded-full" disabled={busy} onClick={() => save(true)} data-testid="planner-notify-yes">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Sì, invia</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function AdminPlanner() {
  const [week, setWeek] = useState(iso(new Date()));
  const [data, setData] = useState(null);
  const [cell, setCell] = useState(null);
  const load = useCallback(() => api.get("/admin/planner", { params: { week } }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))), [week]);
  useEffect(() => { load(); }, [load]);
  const today = iso(new Date());
  const ov = useMemo(() => Object.fromEntries((data?.days || []).map((d) => [d, overlapIds(data, d)])), [data]);
  const shopId = data?.rows.find((r) => r.shop)?.series_id;
  const cellPeople = (r, d) => {
    const own = uniq((r.cells[d] || []).flatMap((e) => e.people));
    const out = r.series_id === shopId ? uniq((data.interventions[d] || []).flatMap((e) => e.people)).filter((p) => !own.some((o) => o.id === p.id)) : [];
    return { own, out };
  };
  const printPdf = async () => {
    const rows = data.rows.map((r) => ({ name: r.name, shop: r.shop, cells: data.days.map((d) => {
      const { own, out } = cellPeople(r, d);
      return [...own, ...out].map((p) => ({ name: p.name, lead: p.lead, warn: ov[d].has(p.id) }));
    }) }));
    const blob = await buildWeekPdf({ title: `Settimana ${dm(data.days[0])} – ${dm(data.days[6])}/${data.days[6].slice(0, 4)}`,
      days: data.days.map((d, i) => `${DOW[i]} ${dm(d)}`), rows });
    downloadBlob(blob, `MDN-Settimana-${data.days[0]}.pdf`);
  };
  return (
    <div data-testid="admin-planner-page">
      <PageHeader eyebrow="Pianificazione" title="Chi lavora dove" />
      <div className="mb-4 flex items-center gap-2">
        <Button variant="outline" size="icon" className="rounded-full" onClick={() => setWeek(shift(week, -7))} data-testid="planner-prev-week"><ChevronLeft className="h-4 w-4" /></Button>
        <button type="button" onClick={() => setWeek(iso(new Date()))} className="min-w-32 text-center text-sm font-semibold" data-testid="planner-range">
          {data ? `${dm(data.days[0])} – ${dm(data.days[6])}` : "…"}</button>
        <Button variant="outline" size="icon" className="rounded-full" onClick={() => setWeek(shift(week, 7))} data-testid="planner-next-week"><ChevronRight className="h-4 w-4" /></Button>
        <span className="ml-auto hidden items-center gap-1 text-xs text-slate-500 sm:flex"><span className="h-2 w-2 rounded-full bg-orange-500" /> impegnato in due posti</span>
        <Button variant="outline" className="rounded-full" disabled={!data?.rows.length} onClick={printPdf} data-testid="planner-pdf-button"><FileDown className="h-4 w-4" /> PDF</Button>
      </div>
      {!data ? <Loader2 className="h-6 w-6 animate-spin text-[#61a229]" /> : (
        <div className="overflow-x-auto rounded-2xl border bg-white">
          <table className="w-full border-collapse text-sm" data-testid="planner-table">
            <thead><tr className="text-xs text-slate-500">
              <th className="sticky left-0 z-10 bg-white p-2" />
              {data.days.map((d, i) => <th key={d} className={`p-2 font-semibold ${d === today ? "text-[#2f5a10]" : ""}`}>{DOW[i]}<span className="block font-normal">{dm(d)}</span></th>)}
            </tr></thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.series_id} className="border-t" data-testid={`planner-row-${r.series_id}`}>
                  <th className="sticky left-0 z-10 min-w-[130px] bg-white p-2 text-left font-semibold">
                    <span className="flex items-center gap-1">{r.shop && <Store className="h-4 w-4 text-amber-700" />}{r.name}</span></th>
                  {data.days.map((d, i) => {
                    const es = r.cells[d] || [];
                    const { own, out } = cellPeople(r, d);
                    const can = d >= today && es.length > 0 && es.every((e) => ["in_attesa", "confermato"].includes(e.status));
                    return (
                      <td key={d} className="min-w-[110px] border-l p-1 align-top">
                        <button type="button" disabled={!can} onClick={() => setCell({ row: r, day: d, idx: i, people: own })}
                          className="flex min-h-10 w-full flex-wrap content-start gap-1 rounded-lg p-1 text-left enabled:hover:bg-[#f2f7ec]" data-testid={`planner-cell-${r.series_id}-${d}`}>
                          {[...own, ...out].map((p) => (
                            <span key={p.id} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] ${p.lead ? "bg-[#e7f1dc] font-semibold text-[#2f5a10]" : "bg-slate-100 text-slate-700"}`}
                              data-testid={`planner-cell-${r.series_id}-${d}-p-${p.id}`}>
                              {ov[d].has(p.id) && <span className="h-2 w-2 rounded-full bg-orange-500" data-testid={`planner-cell-${r.series_id}-${d}-p-${p.id}-overlap`} />}{p.name}
                            </span>
                          ))}
                          {!own.length && !out.length && es.length > 0 && <span className="text-[11px] italic text-amber-700">da assegnare</span>}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
              {!data.rows.length && <tr><td colSpan={8} className="p-8 text-center text-slate-500">Nessun cantiere in questa settimana.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      <p className="mt-3 text-xs text-slate-500">Tocca una casella per cambiare chi lavora quel giorno. Nel cantiere di tipo Negozio compaiono anche i tecnici impegnati negli interventi esterni.</p>
      <EditCell cell={cell} staff={data?.staff || []} onClose={() => setCell(null)} onSaved={() => { setCell(null); load(); }} />
    </div>
  );
}
